// react
import { Dispatch, MouseEvent, ReactElement, SetStateAction, useContext, useState } from 'react';

// types
import {
	Benefit,
	Entity,
	ErrorCause,
	Feedback,
	LoadingStatus,
	TransactionPayload,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, SxProps } from '@mui/material';

// fields
import { AutocompleteTags } from '../../fields/AutocompleteTags/AutocompleteTags';
import { Checkbox } from '../../fields/Checkbox/Checkbox';
import { Input } from '../../fields/Input/Input';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';
import { Empty } from '../../ui/Empty/Empty';
import { IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { TableInfoTransaction } from '../../ui/TableInfoTransaction/TableInfoTransaction';

// utils
import { formatDate } from '../../../utils/dates';
import { postOrGetTags } from '../../../utils/tags';
import { translateError } from '../../../utils/translations';

// theme
import { theme } from '../../../theme';

// props
export interface ValuesProps {
	beneficiary: Entity | string;
	benefit: Benefit | string;
	date: string;
	entity: Entity | string;
	quantity: number;
	tags: string[];
}

export interface FormAddNonCashBenefitProps {
	hasBanner?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
	setStatus: Dispatch<SetStateAction<LoadingStatus>>;
	status: LoadingStatus;
}

export const FormAddNonCashBenefit = ({
	hasBanner = false,
	onClose,
	onSuccess,
	setStatus,
	status,
}: FormAddNonCashBenefitProps) => {
	// hooks
	const { idBeneficiary } = useParams();
	const { idEntity } = useParams();
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { userEntity } = useContext(ContextUserEntity);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [beneficiaries, setBeneficiaries] = useState<Entity[]>([]);
	const [benefits, setBenefits] = useState<Benefit[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState<ValuesProps>({
		beneficiary: '',
		benefit: '',
		date: '',
		entity: '',
		quantity: 1,
		tags: [],
	});

	// vars
	const idForm = 'form-add-non-cash-benefit';
	const hasEntityField = Boolean(!(userEntity || idEntity));

	// params
	const paramDialogBeneficiaryId = searchParams.get('dialogBeneficiary');

	// fetch beneficiaries
	useFetch({
		isEnabled: Boolean(!paramDialogBeneficiaryId && project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name&type=BENEFICIARY`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setBeneficiaries(res.data);
			}
		},
	});

	// fetch entities
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,wallet&type=BORROWER,INTERMEDIARY,PIU,SUB_PIU&populate=wallet`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch benefits
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/benefits?project=${project?.id}&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setBenefits(res.data);
			}
		},
	});

	// options
	const optionsBeneficiaries = beneficiaries.map((beneficiary) => {
		return {
			label: beneficiary.name,
			MenuItemProps: {
				resource: beneficiary,
			},
			value: beneficiary.id,
		};
	});

	const optionsBenefits = benefits.map((benefit) => {
		return {
			label: benefit.name,
			MenuItemProps: {
				resource: benefit,
			},
			value: benefit.id,
		};
	});

	const optionsEntities = entities.map((entity) => {
		return {
			disabled: entity.wallet?.status === 'INACTIVE',
			label: entity.name,
			MenuItemProps: {
				resource: entity,
			},
			value: entity.id,
		};
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector('#form-add-non-cash-benefit') as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// set loader
			setStatus('LOADING');

			let tags: number[] = [];

			if (entries.tags && project) {
				// post or get tags
				const resTags = await postOrGetTags({
					names: (entries.tags as string).split(','),
					project: project.id,
				});

				if (resTags.length) {
					// define tags ids
					tags = resTags.map((tag) => tag.id);
				}
			}

			const payload: TransactionPayload = {
				benefit: Number(entries.benefit),
				date: formatDate({ date: entries.date, format: 'YYYY-MM-DD' }),
				fromEntity: Number(userEntity?.id || idEntity || entries.fromEntity),
				metadata: {
					uuid: crypto.randomUUID(),
				},
				project: project?.id,
				quantity: Number(entries.quantity),
				status: 'COMPLETED',
				tags,
				toEntity: Number(idBeneficiary || paramDialogBeneficiaryId || entries.toEntity),
				type: 'NON_CASH_BENEFIT',
			};

			const fetchAddTransaction = await fetch(`${process.env.REACT_APP_API_URL}/transactions`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(payload),
			});
			const resAddTransaction = await fetchAddTransaction.json();

			if (resAddTransaction.error) {
				throw new Error(resAddTransaction.error.message, {
					cause: {
						id: resAddTransaction.error.id,
					},
				});
			}

			const transaction = resAddTransaction.data;

			// post comments
			if (entries.comments) {
				const fetchPostComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
					method: 'post',
					body: JSON.stringify({
						comment: entries.comments,
						transaction: transaction.id,
					}),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostComment = await fetchPostComment.json();

				if (resPostComment.error) {
					throw new Error(resPostComment.error.message, {
						cause: {
							id: resPostComment.error.id,
						},
					});
				}
			}

			// for each file
			for (let i = 0; i < files.length; i++) {
				// define form data
				const formData = new FormData();

				// define path
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/transactions/non-cash-benefits/${transaction.id}/${year}/${month}/`
				);
				formData.append('file', files[i]);
				formData.append('transaction', String(transaction.id));

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				let fetchPostDoc: Response;

				try {
					fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
						method: 'post',
						body: formData,
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					});
				} catch {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				if (fetchPostDoc.status === 403) {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({ alert: { severity: 'error' }, response: resPostDoc });
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				if (hasBanner) {
					// set success banner
					setBanner({
						hasClose: true,
						message: t('alert.nonCashBenefitRecorded'),
						severity: 'success',
					});
				}

				// set status
				setStatus('SUCCESS');

				// close dialog
				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
				setStatus('ERROR');
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});
			setFeedback(feedbackNew);
			setStatus('ERROR');
		}
	};

	// loading and success states
	if (['LOADING', 'SUCCESS'].includes(status)) {
		const content = {
			children: t('empty.addNonCashBenefit.loading.content'),
		};

		const sxBox: SxProps = {
			marginTop: theme.spacing(4),
			marginBottom: theme.spacing(4),
		};

		const title = {
			children: t('empty.addNonCashBenefit.loading.title'),
		};

		const IconCircleProps: IconCircleProps = {
			size: 'xl',
			type: 'cube',
		};

		if (status === 'SUCCESS') {
			content.children = t('empty.addNonCashBenefit.success.content');
			title.children = t('empty.addNonCashBenefit.success.title');
			IconCircleProps.type = 'check';
			sxBox.marginTop = 0;
			sxBox.marginBottom = 0;
		}

		return (
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Empty content={content} IconCircleProps={IconCircleProps} sxBox={sxBox} title={title} />
				</Grid>
				{status === 'SUCCESS' && (
					<>
						<Grid item xs={12}>
							<TableInfoTransaction
								benefit={values.benefit as Benefit}
								date={values.date}
								fromEntity={values.entity as Entity}
								quantity={values.quantity}
								toEntity={values.beneficiary as Entity}
								type="NON_CASH_BENEFIT"
							/>
						</Grid>
					</>
				)}
			</Grid>
		);
	}

	return (
		<form id={idForm} name="formAddNonCashBenefit" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{hasEntityField && (
					<Grid item xs={12} md={idBeneficiary || paramDialogBeneficiaryId ? 12 : 6}>
						<Select
							id={`${idForm}-from-entity`}
							label={t('entity')}
							name="fromEntity"
							onChange={(e, child) => {
								setValues((prevValues) => {
									return {
										...prevValues,
										entity: (child as ReactElement).props.resource as Entity,
									};
								});
							}}
							options={optionsEntities}
							required={true}
						/>
					</Grid>
				)}
				{!idBeneficiary && !paramDialogBeneficiaryId && (
					<Grid item xs={12} md={hasEntityField ? 6 : 12}>
						<Select
							id={`${idForm}-to-entity`}
							label={t('beneficiary')}
							name="toEntity"
							onChange={(e, child) => {
								setValues((prevValues) => {
									return {
										...prevValues,
										beneficiary: (child as ReactElement).props.resource as Entity,
									};
								});
							}}
							options={optionsBeneficiaries}
							required={true}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-quantity`}
						inputProps={{
							min: 1,
						}}
						label={t('quantity')}
						name="quantity"
						onChange={(e) => {
							setValues((prevValues) => {
								return {
									...prevValues,
									quantity: Number(e.target.value),
								};
							});
						}}
						required={true}
						type="number"
						value={values.quantity}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-benefit`}
						label={t('benefit')}
						name="benefit"
						onChange={(e, child) => {
							setValues((prevValues) => {
								return {
									...prevValues,
									benefit: (child as ReactElement).props.resource as Benefit,
								};
							});
						}}
						options={optionsBenefits}
						required={true}
					/>
				</Grid>

				<Grid item xs={12}>
					<Input
						id={`${idForm}-date`}
						inputProps={{
							max: new Date().toISOString().split('T')[0],
						}}
						label={t('date')}
						name="date"
						onChange={(e) => {
							setValues((prevValues) => {
								return {
									...prevValues,
									date: e.target.value,
								};
							});
						}}
						required={true}
						type="date"
						value={values.date}
					/>
				</Grid>
				<Grid item xs={12}>
					<FilePicker onChange={(e, filesSelected) => setFiles(filesSelected)} />
				</Grid>
				{userProjectPermissions?.comment?.create && (
					<Grid item xs={12}>
						<Input
							id={`${idForm}-comments`}
							label={t('comments')}
							minRows={2}
							multiline={true}
							name="comments"
						/>
					</Grid>
				)}
				{userProjectPermissions?.tag?.create && (
					<Grid item xs={12}>
						<AutocompleteTags
							id={`${idForm}-tags`}
							onChange={(tags) => {
								setValues((prevState) => ({
									...prevState,
									tags,
								}));
							}}
							value={values.tags}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Checkbox
						helperText={t('form.confirmation.helper')}
						id={`${idForm}-confirmation`}
						label={t('form.confirmation.label')}
						name="confirmation"
						required={true}
						sxBox={{
							padding: theme.spacing(2),
						}}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
